import {
  WebGLError,
  WebGLFramebufferIncompleteError,
  validateLastGlCall,
} from '@canvas/error/webgl';

/**
 * Returns the raw image data of an image, as a `Uint8ClampedArray`.
 *
 * @remarks You might be wondering why we're using WebGL to get the raw image data,
 * rather than using a 2D canvas context. The reason is that browsers don't actually
 * provide the exact raw image data when using `CanvasRenderingContext2D.getImageData()`.
 * Instead, the browser may apply color space conversions, gamma correction, and other
 * transformations to the image data before returning it. This leads to slight
 * differences, even across different browsers on the same system.
 *
 * @param image The image to get the raw data from.
 */
export function getRawImageData(
  image: HTMLImageElement | ImageBitmap | HTMLCanvasElement
): Uint8ClampedArray {
  if (image instanceof HTMLCanvasElement) {
    const context = image.getContext('2d');
    if (!context) {
      throw new Error('Could not create 2D canvas context');
    }

    return context.getImageData(0, 0, image.width, image.height).data;
  }

  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2', {
    // Helps ensure that the pixel data we read is consistent
    premultipliedAlpha: false,
    // We don't need antialiasing, and it can slow down the process
    antialias: false,
  });

  if (!gl) {
    throw new Error('Could not create WebGL context');
  }

  const useNaturalSize = !(
    image instanceof ImageBitmap || image instanceof HTMLCanvasElement
  );

  // Use the image's width and height to create a canvas of the same size
  canvas.width = useNaturalSize ? image.naturalWidth : image.width;
  canvas.height = useNaturalSize ? image.naturalHeight : image.height;

  let texture: WebGLTexture | null = null;
  let framebuffer: WebGLFramebuffer | null = null;

  try {
    // Create a texture from the image
    texture = gl.createTexture();
    if (texture === null) {
      throw new WebGLError(gl, 'createTexture');
    }

    gl.bindTexture(gl.TEXTURE_2D, texture);

    // Prevent any sampling between pixels
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);

    // Ensure no color space conversion happens, as we want the raw data
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);

    // Ensure that the alpha channel is not premultiplied, as we want the raw data
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

    // Load the image data into the texture
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, image);
    validateLastGlCall(gl, 'texImage2D');

    // Create a framebuffer which we'll use to read the image data
    framebuffer = gl.createFramebuffer();
    if (framebuffer === null) {
      throw new WebGLError(gl, 'createFramebuffer');
    }

    // Attach the texture to the framebuffer
    // eslint rule disabled since this is a separate, temporary WebGL context
    // eslint-disable-next-line no-restricted-syntax
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(
      gl.READ_FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0
    );
    validateLastGlCall(gl, 'framebufferTexture2D');

    // Make sure the framebuffer is complete
    const status = gl.checkFramebufferStatus(gl.READ_FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new WebGLFramebufferIncompleteError(gl, status);
    }

    // Read the image data from the framebuffer
    const pixels = new Uint8ClampedArray(4 * canvas.width * canvas.height);
    gl.readPixels(0, 0, canvas.width, canvas.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    validateLastGlCall(gl, 'readPixels');

    return pixels;
  } finally {
    // Clean up
    if (framebuffer != null) {
      // eslint rule disabled since this is a separate, temporary WebGL context
      // eslint-disable-next-line no-restricted-syntax
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
      gl.deleteFramebuffer(framebuffer);
    }
    if (texture != null) {
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.deleteTexture(texture);
    }

    canvas.width = 1;
    canvas.height = 1;
  }
}
